import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaStar, FaQuoteLeft, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Spinner from '../Spinner';
import './Clients.css';

const Clients = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [clients, setClients] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClientIndex, setSelectedClientIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef(null);

  // Get current language
  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  // Default/fallback clients
  const defaultClients = [
    {
      name: 'LVMH',
      logoUrl: '/assets/images/logos/lvmh-logo.png',
      rating: 5
    },
    {
      name: 'Groupe Rocher',
      logoUrl: '/assets/images/logos/groupe-rocher-logo.png',
      rating: 5
    },
    {
      name: 'Groupe Rossel',
      logoUrl: '/assets/images/logos/rossel-logo.png',
      rating: 5
    },
    {
      name: 'Cheuvreux',
      logoUrl: '/assets/images/logos/cheuvreux-logo.png',
      rating: 5
    }
  ];

  // Fetch data from Firestore once
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const q = query(collection(db, 'clients'), orderBy('order', 'asc'));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const items = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setRawData(items);
        } else {
          setRawData([]);
        }
      } catch (err) {
        console.log('Using default clients (Firestore not configured or empty)');
        setRawData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  // Update clients when language changes or rawData updates
  useEffect(() => {
    if (rawData.length > 0) {
      const localizedItems = rawData.map(item => {
        const successStory = currentLang === 'fr'
          ? (item.successStory_fr || item.successStory_en || item.successStory)
          : (item.successStory_en || item.successStory);

        const storyAuthor = currentLang === 'fr'
          ? (item.storyAuthor_fr || item.storyAuthor_en || item.storyAuthor)
          : (item.storyAuthor_en || item.storyAuthor);

        return {
          ...item,
          successStory,
          storyAuthor
        };
      });
      setClients(localizedItems);
    } else if (!loading) {
      setClients(defaultClients);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData, currentLang, loading]);

  // Check scroll buttons after clients load
  useEffect(() => {
    if (clients.length > 0) {
      setTimeout(checkScrollButtons, 100);
    }
  }, [clients]);

  const renderStars = (rating) => {
    return Array.from({ length: rating || 5 }, (_, index) => (
      <FaStar key={index} className="star" />
    ));
  };

  const handleClientClick = (index) => {
    setSelectedClientIndex(index);
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 280; // Card width + gap
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScrollButtons, 300);
    }
  };

  if (loading) {
    return (
      <section id="clients" className="clients">
        <div className="container">
          <Spinner size="medium" text={t('clients.loading')} />
        </div>
      </section>
    );
  }

  const isHorizontalScroll = clients.length > 4;

  // Get the currently selected client for the testimonial section
  const displayedClient = clients[selectedClientIndex] || clients[0];

  const clientsContent = (
    <div className={`clients-grid ${isHorizontalScroll ? 'horizontal-scroll' : ''}`}>
      {clients.map((client, index) => (
        <motion.div
          key={client.id || index}
          className={`client-card clickable ${selectedClientIndex === index ? 'selected' : ''}`}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: Math.min(index * 0.1, 0.4) }}
          onClick={() => handleClientClick(index)}
        >
          <div className="client-logo">
            <img src={client.logoUrl || client.logo} alt={client.name} />
          </div>
          <h3>{client.name}</h3>
          <div className="rating">
            {renderStars(client.rating)}
          </div>
          {client.successStory && (
            <div className="view-story-hint">
              {t('clients.viewTestimonial')}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );

  return (
    <section id="clients" className="clients" ref={ref}>
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {t('clients.title')}
        </motion.h2>

        <motion.p
          className="section-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {t('clients.subtitle')}
        </motion.p>

        {isHorizontalScroll ? (
          <div className="clients-scroll-wrapper">
            <button
              className="scroll-arrow left"
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
            >
              <FaChevronLeft />
            </button>

            <div
              className="clients-scroll-container"
              ref={scrollContainerRef}
              onScroll={checkScrollButtons}
            >
              {clientsContent}
            </div>

            <button
              className="scroll-arrow right"
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              aria-label="Scroll right"
            >
              <FaChevronRight />
            </button>
          </div>
        ) : (
          clientsContent
        )}

        {/* Show selected client's testimonial in the bottom section */}
        <AnimatePresence mode="wait">
          {rawData.length > 0 && displayedClient?.successStory ? (
            <motion.div
              key={selectedClientIndex}
              className="success-story"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="story-content">
                <FaQuoteLeft className="quote-icon" />
                <p>{displayedClient.successStory}</p>
                <cite>{displayedClient.storyAuthor}</cite>
              </div>
            </motion.div>
          ) : rawData.length === 0 ? (
            <motion.div
              className="success-story"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="story-content">
                <FaQuoteLeft className="quote-icon" />
                <p>{t('clients.successStory.quote')}</p>
                <cite>{t('clients.successStory.author')}</cite>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Clients;
